import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components/macro";
import { Table } from "../../../../components/Table/Table";
import { TagsContainer } from "../../../../components/AttributeGroupFormGenerator/DocumentAttribute/DocumentAttributePreview";
import { getObjectReferenceDetailPath } from "../../../../components/ObjectReference/ObjectReferencePreviewTooltip";
import { useMdmEntityDisplayNames } from "../../../../util/mdmEntityDisplayNames";
import { useRoutePath } from "../../../../util/Routing";
import { EMPTY_VALUE } from "./utils";
import { RelatedEntitiesSection, RelatedEntitiesSourceProfile } from "./types";

type MergedSection = {
  key: string;
  displayName: string;
  beforeProfiles: RelatedEntitiesSourceProfile[];
  afterProfiles: RelatedEntitiesSourceProfile[];
};

type RelatedEntitiesTableRow = {
  name: string;
  beforeProfiles: RelatedEntitiesSourceProfile[];
  afterProfiles: RelatedEntitiesSourceProfile[];
};

type ProfileTagVariant = "standard" | "added" | "removed";

const profileTagStyles = css<{ $variant: ProfileTagVariant }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  background: ${({ theme, $variant }) =>
    $variant === "removed"
      ? theme.resolve("color_bg_surface_error_diff")
      : $variant === "added"
      ? theme.resolve("color_bg_surface_success_diff")
      : theme.resolve("color_bg_surface_standard")};
  border: 1px solid ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  white-space: nowrap;
`;

const ProfileTag = styled(Link)<{ $variant: ProfileTagVariant }>`
  ${profileTagStyles};
  text-decoration: none;
  cursor: pointer;
`;

const ProfileTagText = styled.span<{ $variant: ProfileTagVariant }>`
  ${profileTagStyles};
`;

function getSectionObjectType(
  beforeProfiles: RelatedEntitiesSourceProfile[],
  afterProfiles: RelatedEntitiesSourceProfile[]
): string | undefined {
  return (
    beforeProfiles.find((profile) => profile.object_type)?.object_type ??
    afterProfiles.find((profile) => profile.object_type)?.object_type
  );
}

function formatRelatedEntitiesSectionName(
  objectType: string | undefined,
  fallbackName: string,
  t: TFunction,
  getObjectTypeLabel: (
    objectType: string | undefined,
    form?: "singular" | "plural"
  ) => string
): string {
  if (objectType === "product") {
    return t("Related Products");
  }
  if (
    objectType === "customer" ||
    objectType === "distributor" ||
    objectType === "supplier"
  ) {
    return `${t("Primary")} ${getObjectTypeLabel(objectType, "plural")}`;
  }
  return fallbackName;
}

function sourceProfilePath(
  adminPath: string,
  source: RelatedEntitiesSourceProfile
): string | null {
  if (!source.id || !source.object_type) {
    return null;
  }
  if (source.object_type === "product") {
    return `${adminPath}/pim/products/${source.object_number ?? source.id}`;
  }
  return getObjectReferenceDetailPath(adminPath, source.object_type, source.id);
}

function dedupeProfiles(
  profiles: RelatedEntitiesSourceProfile[]
): RelatedEntitiesSourceProfile[] {
  const seen = new Set<string>();
  return profiles.filter((profile) => {
    if (!profile.id || seen.has(profile.id)) {
      return false;
    }
    seen.add(profile.id);
    return true;
  });
}

function mergeRelatedEntitySections(
  beforeSections: RelatedEntitiesSection[],
  afterSections: RelatedEntitiesSection[]
): MergedSection[] {
  const map = new Map<string, MergedSection>();
  const order: string[] = [];

  const getOrCreate = (section: RelatedEntitiesSection) => {
    const key = section.attribute_id || section.target_side_display_name;
    if (!map.has(key)) {
      map.set(key, {
        key,
        displayName: section.target_side_display_name,
        beforeProfiles: [],
        afterProfiles: [],
      });
      order.push(key);
    }
    return map.get(key)!;
  };

  for (const section of beforeSections) {
    const merged = getOrCreate(section);
    for (const action of section.actions ?? []) {
      if (action.source_profile) {
        merged.beforeProfiles.push(action.source_profile);
      }
    }
  }

  for (const section of afterSections) {
    const merged = getOrCreate(section);
    for (const action of section.actions ?? []) {
      if (action.source_profile) {
        merged.afterProfiles.push(action.source_profile);
      }
    }
  }

  return order.map((key) => {
    const section = map.get(key)!;
    return {
      ...section,
      beforeProfiles: dedupeProfiles(section.beforeProfiles),
      afterProfiles: dedupeProfiles(section.afterProfiles),
    };
  });
}

function getTagVariant(
  side: "before" | "after",
  isChanged: boolean
): ProfileTagVariant {
  if (!isChanged) {
    return "standard";
  }
  return side === "before" ? "removed" : "added";
}

function ProfileNameList({
  profiles,
  compareIds,
  side,
  adminPath,
}: {
  profiles: RelatedEntitiesSourceProfile[];
  compareIds: Set<string>;
  side: "before" | "after";
  adminPath: string;
}) {
  if (!profiles.length) {
    return <span>{EMPTY_VALUE}</span>;
  }

  return (
    <TagsContainer>
      {profiles.map((profile, index) => {
        const path = sourceProfilePath(adminPath, profile);
        const isChanged = !compareIds.has(profile.id);
        const variant = getTagVariant(side, isChanged);

        if (path) {
          return (
            <ProfileTag
              key={`${side}-${profile.id}-${index}`}
              to={path}
              $variant={variant}
            >
              {profile.name}
            </ProfileTag>
          );
        }

        return (
          <ProfileTagText
            key={`${side}-${profile.id}-${index}`}
            $variant={variant}
          >
            {profile.name}
          </ProfileTagText>
        );
      })}
    </TagsContainer>
  );
}

export function RelatedEntitiesActivity({
  sections,
}: {
  sections: {
    before: RelatedEntitiesSection[];
    after: RelatedEntitiesSection[];
  };
}) {
  const { t } = useTranslation();
  const { adminPath } = useRoutePath();
  const { getObjectTypeLabel } = useMdmEntityDisplayNames();
  const mergedSections = mergeRelatedEntitySections(
    sections.before ?? [],
    sections.after ?? []
  );

  if (!mergedSections.length) {
    return null;
  }

  const tableData: RelatedEntitiesTableRow[] = mergedSections.map((section) => {
    const objectType = getSectionObjectType(
      section.beforeProfiles,
      section.afterProfiles
    );
    return {
      name: formatRelatedEntitiesSectionName(
        objectType,
        section.displayName,
        t,
        getObjectTypeLabel
      ),
      beforeProfiles: section.beforeProfiles,
      afterProfiles: section.afterProfiles,
    };
  });

  const columns: ColumnDef<RelatedEntitiesTableRow>[] = [
    {
      header: t("Name"),
      accessorKey: "name",
      cell: ({ row }) => <span>{row.original.name}</span>,
    },
    {
      header: t("Before"),
      accessorKey: "beforeProfiles",
      cell: ({ row }) => {
        const afterIds = new Set(
          row.original.afterProfiles.map((profile) => profile.id)
        );
        return (
          <ProfileNameList
            profiles={row.original.beforeProfiles}
            compareIds={afterIds}
            side="before"
            adminPath={adminPath}
          />
        );
      },
    },
    {
      header: t("After"),
      accessorKey: "afterProfiles",
      cell: ({ row }) => {
        const beforeIds = new Set(
          row.original.beforeProfiles.map((profile) => profile.id)
        );
        return (
          <ProfileNameList
            profiles={row.original.afterProfiles}
            compareIds={beforeIds}
            side="after"
            adminPath={adminPath}
          />
        );
      },
    },
  ];

  return (
    <Table
      columns={columns}
      data={tableData}
      isLoading={false}
      error={undefined}
      enableDefaultSort={false}
      enableColumnResize={false}
      lastChildleftAlign={true}
      cellVerticalAlignTop={true}
    />
  );
}
